import { Badge } from '@/components/ui/badge';
import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useFavoritesStore } from '@/store/favoritesStore';
import { Product } from '@/types/product';
import {
  ClockIcon,
  FilterIcon,
  GivePillIcon,
  SearchIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Heart, Loader2 } from 'lucide-react';

interface SearchResultsProps {
  value: string;
  filteredProducts: Product[];
  recentSearches: string[];
  prescriptionCount: number;
  otcCount: number;
  setPrescriptionFilter: (value: boolean | null) => void;
  setOpen?: (open: boolean) => void;
  isLoading?: boolean;
  onSearchSelect?: (query: string) => void;
}

const formatPrice = (price: number | null | undefined): string => {
  return (price ?? 0).toLocaleString();
};

export function SearchResults({
  value,
  filteredProducts,
  recentSearches,
  prescriptionCount,
  otcCount,
  setPrescriptionFilter,
  setOpen = () => {},
  isLoading = false,
  onSearchSelect,
}: SearchResultsProps) {
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();

  const handleSelect = (callback: () => void) => {
    callback();
  };

  return (
    <CommandList>
      {isLoading ? (
        <div className="py-6 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <p className="captionmedium text-muted-foreground mt-2">
            Searching...
          </p>
        </div>
      ) : (
        <>
          {value.length === 0 && recentSearches.length > 0 && (
            <CommandGroup heading="Recent Searches">
              {recentSearches.map(search => {
                const fav = isFavorite(search);
                return (
                  <CommandItem
                    key={search}
                    onSelect={() =>
                      handleSelect(() => onSearchSelect?.(search))
                    }
                    className="flex items-center"
                  >
                    <HugeiconsIcon
                      icon={ClockIcon}
                      size={16}
                      className="mr-2"
                      aria-hidden="true"
                    />
                    <span>{search}</span>
                    <Heart
                      size={16}
                      className={`ml-auto cursor-pointer transition-colors ${
                        fav
                          ? 'fill-[var(--famasi-red-500)] text-[var(--famasi-red-500)]'
                          : 'text-muted-foreground hover:text-[var(--famasi-red-500)]'
                      }`}
                      aria-label={
                        fav ? 'Remove from favorites' : 'Add to favorites'
                      }
                      onClick={e => {
                        e.stopPropagation();
                        fav
                          ? removeFavorite(search)
                          : addFavorite({
                              id: search,
                              title: search,
                              price: 0,
                              image: '',
                              category: '',
                              prescription: false,
                            });
                      }}
                      fill={fav ? 'currentColor' : 'none'}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          <CommandGroup heading="Quick Filters">
            <CommandItem
              onSelect={() => handleSelect(() => setPrescriptionFilter(true))}
              className="flex items-center cursor-pointer"
            >
              <HugeiconsIcon
                icon={GivePillIcon}
                size={16}
                className="mr-2"
                aria-hidden="true"
              />
              <span>Prescription Only</span>
              <Badge variant="secondary" className="ml-auto">
                {prescriptionCount}
              </Badge>
            </CommandItem>
            <CommandItem
              onSelect={() => handleSelect(() => setPrescriptionFilter(false))}
              className="flex items-center cursor-pointer"
            >
              <HugeiconsIcon
                icon={FilterIcon}
                size={16}
                className="mr-2"
                aria-hidden="true"
              />
              <span>Over the Counter</span>
              <Badge variant="secondary" className="ml-auto">
                {otcCount}
              </Badge>
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          {filteredProducts.length > 0 ? (
            <CommandGroup heading={`Results (${filteredProducts.length})`}>
              {filteredProducts.map(product => (
                <CommandItem
                  key={product.id}
                  onSelect={() =>
                    handleSelect(() => {
                      window.location.href = `/product/${product.id}`;
                    })
                  }
                  className="flex items-center cursor-pointer"
                >
                  <HugeiconsIcon
                    icon={SearchIcon}
                    size={16}
                    className="mr-2"
                    aria-hidden="true"
                  />
                  <span>{product.title}</span>
                  <span className="ml-2 text-muted-foreground">
                    ₦{formatPrice(product.price)}
                  </span>
                  <Badge
                    variant={product.prescription ? 'destructive' : 'secondary'}
                    className="ml-auto"
                  >
                    {product.prescription ? 'Rx' : 'OTC'}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : value.length > 0 ? (
            <div className="py-6 text-center">
              <p>No results found.</p>
              <p className="captionmedium text-muted-foreground mt-1">
                Try a different search term.
              </p>
            </div>
          ) : null}
        </>
      )}
    </CommandList>
  );
}
